import { orgScopedPermission, platformPermission } from "./jwt";
import {
  PICKUP_ORDER_LIST_ROLES,
  USER_MANAGEMENT_ROLES,
} from "./legacy-roles";
import type { LegacyRole } from "./types";

export type NavAccessType =
  | "platform"
  | "orgMembership"
  | "orgScoped"
  | "platformScoped";

export interface NavItemDef {
  text: string;
  path: string;
  accessType: NavAccessType;
  /** For orgScoped: entity + action */
  entity?: string;
  action?: string;
  legacyRoles?: readonly LegacyRole[];
}

export interface NavGroupDef {
  text: string;
  subItems: NavItemDef[];
}

export type MenuEntry = NavItemDef | NavGroupDef;

export function isNavGroup(entry: MenuEntry): entry is NavGroupDef {
  return "subItems" in entry;
}

/** Resolve permission pattern for org-scoped nav items. */
export function resolveNavPermission(item: NavItemDef, orgId: string | null): string | null {
  if (item.accessType === "platform" || item.accessType === "platformScoped") {
    if (!item.entity || !item.action) {
      return null;
    }
    return platformPermission(item.entity, item.action);
  }
  if (item.accessType === "orgScoped" && orgId && item.entity && item.action) {
    return orgScopedPermission(orgId, item.entity, item.action);
  }
  return null;
}

/**
 * Sidebar permission catalog — single source of truth.
 * Remaining pages without explicit action gates: see checklist in comments below.
 */
export const NAV_CATALOG: MenuEntry[] = [
  {
    text: "Dashboard",
    path: "/",
    accessType: "orgScoped",
    entity: "dashboard",
    action: "read",
  },
  {
    text: "Organizations",
    path: "/organizations",
    accessType: "orgMembership",
  },
  {
    text: "Users",
    path: "/users",
    accessType: "orgScoped",
    entity: "user",
    action: "list",
    legacyRoles: USER_MANAGEMENT_ROLES,
  },
  {
    text: "API Keys",
    path: "/api-keys",
    accessType: "platformScoped",
    entity: "api_key",
    action: "list",
  },
  {
    text: "Notifications",
    path: "/notifications/send",
    accessType: "platformScoped",
    entity: "notification",
    action: "send",
  },
  {
    text: "Approvals",
    path: "/approvals",
    accessType: "platformScoped",
    entity: "approval",
    action: "list",
  },
  {
    text: "Verification",
    path: "/verification/batches",
    accessType: "platformScoped",
    entity: "verification.batch",
    action: "list",
  },
  {
    text: "Finance",
    path: "/finance",
    accessType: "platform",
  },
  {
    text: "Operations",
    subItems: [
      {
        text: "Waste movers",
        path: "/operations/waste-movers",
        accessType: "platform",
      },
      {
        text: "Waste outlets",
        path: "/operations/waste-outlets",
        accessType: "platform",
      },
      {
        text: "Recyclers",
        path: "/operations/recyclers",
        accessType: "platform",
      },
      {
        text: "Supplier links",
        path: "/operations/supplier-links",
        accessType: "platform",
      },
      {
        text: "Facility links",
        path: "/operations/facility-links",
        accessType: "platform",
      },
    ],
  },
  {
    text: "Orders",
    subItems: [
      {
        text: "Pickup Orders",
        path: "/pickup_orders",
        accessType: "orgScoped",
        entity: "pickup_order",
        action: "list",
        legacyRoles: PICKUP_ORDER_LIST_ROLES,
      },
      {
        text: "Batch Complete",
        path: "/deliveries/aggregations",
        accessType: "platformScoped",
        entity: "delivery",
        action: "complete",
      },
      {
        text: "Select Batch Complete",
        path: "/pickup_orders/batch-complete",
        accessType: "platformScoped",
        entity: "delivery",
        action: "complete",
      },
      {
        text: "Pre Order",
        path: "/pre_orders",
        accessType: "orgScoped",
        entity: "pre_order",
        action: "list",
      },
      {
        text: "Purchase Order",
        path: "/orders",
        accessType: "orgScoped",
        entity: "purchase_order",
        action: "list",
      },
      {
        text: "Invoices",
        path: "/invoices",
        accessType: "orgScoped",
        entity: "invoice",
        action: "list",
      },
    ],
  },
  {
    text: "Transportation",
    subItems: [
      {
        text: "Fleet",
        path: "/transportation/fleet",
        accessType: "orgScoped",
        entity: "truck",
        action: "list",
      },
      {
        text: "Drivers",
        path: "/transportation/drivers",
        accessType: "orgScoped",
        entity: "driver",
        action: "list",
      },
      {
        text: "Order Tracking",
        path: "/deliveries",
        accessType: "orgScoped",
        entity: "delivery",
        action: "list",
      },
      {
        text: "Truck Tracking",
        path: "/truck_tracking",
        accessType: "orgScoped",
        entity: "truck",
        action: "list",
      },
    ],
  },
  {
    text: "Recycling",
    subItems: [
      {
        text: "Waste Types",
        path: "/recycling/waste-types",
        accessType: "platformScoped",
        entity: "waste_catalog",
        action: "list",
      },
      {
        text: "Organization Waste Types",
        path: "/recycling/materials",
        accessType: "orgScoped",
        entity: "material_type",
        action: "list",
      },
    ],
  },
  {
    text: "Containers",
    subItems: [
      {
        text: "Containers",
        path: "/containers/list",
        accessType: "orgScoped",
        entity: "container",
        action: "list",
      },
      {
        text: "Collection Sites",
        path: "/containers/sites",
        accessType: "orgScoped",
        entity: "collection_site",
        action: "list",
      },
    ],
  },
  {
    text: "Devices",
    subItems: [
      {
        text: "Devices",
        path: "/devices",
        accessType: "platformScoped",
        entity: "device",
        action: "list",
      },
      {
        text: "Device Types",
        path: "/devices/types",
        accessType: "platformScoped",
        entity: "device_type",
        action: "list",
      },
    ],
  },
  {
    text: "ESG",
    subItems: [
      {
        text: "MRV Methodologies",
        path: "/esg/mrv-methodologies",
        accessType: "platformScoped",
        entity: "esg_mrv_methodology",
        action: "list",
      },
      {
        text: "Emission Factors",
        path: "/esg/emission-factors",
        accessType: "platformScoped",
        entity: "esg_emission_factor",
        action: "list",
      },
      {
        text: "Utility Emission Factors",
        path: "/esg/utility-emission-factors",
        accessType: "platformScoped",
        entity: "esg_utility_factor",
        action: "list",
      },
    ],
  },
];

/** Platform permissions for org create/delete (action gates on Organizations page). */
export const PERM_ORG_CREATE = platformPermission("organization", "create");
export const PERM_ORG_DELETE = platformPermission("organization", "delete");
export const PERM_ORG_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "organization", "write");
export const PERM_ORG_READ = (orgId: string) =>
  orgScopedPermission(orgId, "organization", "read");

export const PERM_ADMIN_USER_LIST = platformPermission("user", "list");
export const PERM_USER_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "user", "list");
export const PERM_USER_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "user", "write");
export const PERM_ROLE_ASSIGN = (orgId: string) =>
  orgScopedPermission(orgId, "role", "assign");
export const PERM_USER_DELETE = platformPermission("user", "delete");

export const PERM_PICKUP_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "pickup_order", "list");
export const PERM_PICKUP_CREATE = (orgId: string) =>
  orgScopedPermission(orgId, "pickup_order", "create");
export const PERM_PICKUP_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "pickup_order", "write");
export const PERM_PICKUP_COMPLETE = (orgId: string) =>
  orgScopedPermission(orgId, "pickup_order", "complete");
export const PERM_PICKUP_DELETE = (orgId: string) =>
  orgScopedPermission(orgId, "pickup_order", "delete");
export const PERM_PICKUP_REJECT = (orgId: string) =>
  orgScopedPermission(orgId, "pickup_order", "reject");
export const PERM_PICKUP_ROLLBACK_AFTER_PICKUP = (orgId: string) =>
  orgScopedPermission(orgId, "pickup_order", "rollback_after_pickup");
export const PERM_ADMIN_PICKUP_ASSIGN = platformPermission("pickup_order", "assign");
// Historical (backfilled) order creation is platform-admin-only on the backend
// (is_admin_required=True on POST /pickup_order/admin/historical) -- unlike
// PERM_PICKUP_CREATE, there is no org-scoped/tenant-role fallback for this one.
export const PERM_ADMIN_PICKUP_CREATE_HISTORICAL = platformPermission("pickup_order", "create");

export const PERM_PURCHASE_ORDER_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "purchase_order", "list");
export const PERM_OFFER_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "offer", "list");

export const PERM_PRE_ORDER_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "pre_order", "list");
export const PERM_PRE_ORDER_READ = (orgId: string) =>
  orgScopedPermission(orgId, "pre_order", "read");
export const PERM_PRE_ORDER_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "pre_order", "write");
export const PERM_PRE_ORDER_CREATE = (orgId: string) =>
  orgScopedPermission(orgId, "pre_order", "create");

export const PERM_ADMIN_PRE_ORDER_LIST = platformPermission("pre_order", "list");
export const PERM_ADMIN_PRE_ORDER_READ = platformPermission("pre_order", "read");
export const PERM_ADMIN_PRE_ORDER_WRITE = platformPermission("pre_order", "write");

export const PERM_ESG_EMISSION_FACTOR_MANAGE = platformPermission(
  "esg_emission_factor",
  "manage",
);
export const PERM_ESG_EMISSION_FACTOR_LIST = platformPermission(
  "esg_emission_factor",
  "list",
);
export const PERM_ESG_EMISSION_FACTOR_CREATE = platformPermission(
  "esg_emission_factor",
  "create",
);
export const PERM_ESG_EMISSION_FACTOR_WRITE = platformPermission(
  "esg_emission_factor",
  "write",
);
export const PERM_ESG_EMISSION_FACTOR_APPROVE = platformPermission(
  "esg_emission_factor",
  "approve",
);
export const PERM_ESG_UTILITY_FACTOR_LIST = platformPermission("esg_utility_factor", "list");
export const PERM_ESG_UTILITY_FACTOR_READ = platformPermission("esg_utility_factor", "read");
export const PERM_ESG_UTILITY_FACTOR_CREATE = platformPermission("esg_utility_factor", "create");
export const PERM_ESG_UTILITY_FACTOR_WRITE = platformPermission("esg_utility_factor", "write");
export const PERM_ESG_UTILITY_FACTOR_APPROVE = platformPermission(
  "esg_utility_factor",
  "approve",
);

export const PERM_ESG_MRV_LIST = platformPermission("esg_mrv_methodology", "list");
export const PERM_WASTE_CATALOG_LIST = platformPermission("waste_catalog", "list");
export const PERM_ESG_MRV_READ = platformPermission("esg_mrv_methodology", "read");
export const PERM_ESG_MRV_CREATE = platformPermission("esg_mrv_methodology", "create");
export const PERM_ESG_MRV_WRITE = platformPermission("esg_mrv_methodology", "write");
export const PERM_ESG_MRV_APPROVE = platformPermission("esg_mrv_methodology", "approve");

export const PERM_ESG_BASELINE_READ = platformPermission("esg_baseline_ef", "read");
export const PERM_ESG_BASELINE_WRITE = platformPermission("esg_baseline_ef", "write");
export const PERM_ESG_BASELINE_APPROVE = platformPermission("esg_baseline_ef", "approve");

export const PERM_APPROVAL_LIST = platformPermission("approval", "list");
export const PERM_FLOW_STEP_TYPE_LIST = platformPermission("flow_step_type", "list");
export const PERM_APPROVAL_READ = platformPermission("approval", "read");
export const PERM_APPROVAL_SUBMIT = platformPermission("approval", "submit");
export const PERM_APPROVAL_WRITE = platformPermission("approval", "write");

export const PERM_VERIFICATION_BATCH_LIST = platformPermission("verification.batch", "list");
export const PERM_VERIFICATION_BATCH_READ = platformPermission("verification.batch", "read");
export const PERM_VERIFICATION_CALCULATION_RUN_READ = platformPermission(
  "verification.calculation_run",
  "read",
);

export const PERM_API_KEY_LIST = platformPermission("api_key", "list");
export const PERM_NOTIFICATION_SEND = platformPermission("notification", "send");
export const PERM_API_KEY_CREATE = platformPermission("api_key", "create");
export const PERM_API_KEY_READ = platformPermission("api_key", "read");
export const PERM_API_KEY_WRITE = platformPermission("api_key", "write");
export const PERM_API_KEY_DELETE = platformPermission("api_key", "delete");

export const PERM_ADMIN_DELIVERY_COMPLETE = platformPermission("delivery", "complete");

export const PERM_DEVICE_LIST = platformPermission("device", "list");
export const PERM_DEVICE_READ = platformPermission("device", "read");
export const PERM_DEVICE_CREATE = platformPermission("device", "create");
export const PERM_DEVICE_WRITE = platformPermission("device", "write");
export const PERM_DEVICE_DELETE = platformPermission("device", "delete");
export const PERM_DEVICE_TYPE_LIST = platformPermission("device_type", "list");
export const PERM_DEVICE_TYPE_CREATE = platformPermission("device_type", "create");
export const PERM_DEVICE_TYPE_WRITE = platformPermission("device_type", "write");
export const PERM_DEVICE_TYPE_DELETE = platformPermission("device_type", "delete");

/*
 * Action-gate checklist (pages not yet wired — nav gating covers access):
 * - processing/*, recycling/Quality, recycling/SortingLines
 * - settings/Profile, knowledge base pages
 * - operations detail pages (inherit platform admin / dashboard.read)
 */
